import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Settings } from 'lucide-react';
import { Tutors } from '../components/settings/Tutors';
import { Account } from '../components/settings/Account';
import { Billing } from '../components/settings/Billing';
import { Users } from '../components/settings/Users';
import { Invitations } from '../components/settings/Invitations';
import { AccountDetails } from '../components/settings/AccountDetails';
import { EducationSystemSettings } from '../components/settings/EducationSystemSettings';
import { ExternalResourcesSettings } from '../components/settings/ExternalResourcesSettings';
import { SettingsTilesLanding } from '../components/settings/SettingsTilesLanding';
import { SettingsSectionLayout } from '../components/settings/SettingsSectionLayout';
import { SettingsUsersHub } from '../components/settings/SettingsUsersHub';
import { parseSettingsPath, settingsTiles, type SettingsTile } from '../config/settingsTiles';
import { useLazyQuery } from '@soap/modules';
import { useUserRole } from '../../hooks/useUserRole';

const sectionTitle: Record<string, string> = {
  profile: 'Your Profile',
  billing: 'Billing',
  'education-system': 'Education System',
  'external-resources': 'External Resources',
  'users-hub': 'Users',
  'users-account-details': 'Account Details',
  'users-members': 'Users',
  'users-invitations': 'Invitations',
  tutors: 'Tutors'
};

export function SettingsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  // Second arg `{}` fetches on mount; avoid useEffect(getProfile) — trigger ref can change each render and loop.
  const [, e138GotProfile] = useLazyQuery('C166v1_GetUserProfile', {});
  const { requestingUserIsOrgAdmin } = useUserRole();
  const [section, setSection] = useState(() => parseSettingsPath(location.pathname));

  useEffect(() => {
    setSection(parseSettingsPath(location.pathname));
  }, [location.pathname]);

  const hasSubAccounts = e138GotProfile?.e138_IsOnAPlanWithSubAccounts === true;

  const visibleTiles = useMemo(() => {
    return settingsTiles.filter((tile: SettingsTile) => {
      if (tile.requiresSubAccounts && !hasSubAccounts) return false;
      if (tile.requiresOrgAdmin && (!hasSubAccounts || !requestingUserIsOrgAdmin)) return false;
      return true;
    });
  }, [hasSubAccounts, requestingUserIsOrgAdmin]);

  const goLanding = () => {
    navigate('/app/settings');
  };

  // Redirect if user hits a restricted URL
  useEffect(() => {
    const s = parseSettingsPath(location.pathname);
    if (
      (s === 'users-hub' ||
        s === 'users-account-details' ||
        s === 'users-members' ||
        s === 'users-invitations') &&
      !hasSubAccounts
    ) {
      navigate('/app/settings', { replace: true });
    }
    if ((s === 'users-members' || s === 'users-invitations') && !requestingUserIsOrgAdmin) {
      navigate('/app/settings', { replace: true });
    }
  }, [location.pathname, hasSubAccounts, requestingUserIsOrgAdmin, navigate]);

  const renderSection = () => {
    switch (section) {
      case 'landing':
        return <SettingsTilesLanding visibleTiles={visibleTiles} />;
      case 'profile':
        return (
          <SettingsSectionLayout title={sectionTitle.profile} onBack={goLanding}>
            <Account />
          </SettingsSectionLayout>
        );
      case 'billing':
        return (
          <SettingsSectionLayout title={sectionTitle.billing} onBack={goLanding}>
            <Billing />
          </SettingsSectionLayout>
        );
      case 'education-system':
        return (
          <SettingsSectionLayout title={sectionTitle['education-system']} onBack={goLanding}>
            <EducationSystemSettings />
          </SettingsSectionLayout>
        );
      case 'external-resources':
        return (
          <SettingsSectionLayout title={sectionTitle['external-resources']} onBack={goLanding}>
            <ExternalResourcesSettings />
          </SettingsSectionLayout>
        );
      case 'users-hub':
        return (
          <SettingsSectionLayout title={sectionTitle['users-hub']} onBack={goLanding}>
            <SettingsUsersHub showOrgAdminSections={requestingUserIsOrgAdmin === true} />
          </SettingsSectionLayout>
        );
      case 'users-account-details':
        return (
          <SettingsSectionLayout title={sectionTitle['users-account-details']} onBack={goLanding}>
            <AccountDetails />
          </SettingsSectionLayout>
        );
      case 'users-members':
        return (
          <SettingsSectionLayout title={sectionTitle['users-members']} onBack={goLanding}>
            <Users />
          </SettingsSectionLayout>
        );
      case 'users-invitations':
        return (
          <SettingsSectionLayout title={sectionTitle['users-invitations']} onBack={goLanding}>
            <Invitations />
          </SettingsSectionLayout>
        );
      case 'tutors':
        return (
          <SettingsSectionLayout title={sectionTitle.tutors} onBack={goLanding}>
            <Tutors />
          </SettingsSectionLayout>
        );
      default:
        return <SettingsTilesLanding visibleTiles={visibleTiles} />;
    }
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="flex items-center mb-6">
        <Settings className="mr-3 text-action-blue" size={24} />
        <h1 className="text-2xl font-bold text-gray-900">Control Panel</h1>
      </div>
      {renderSection()}
    </div>
  );
}
